import Add16 from '@carbon/icons-react/es/add/16';
import { useBoolean } from 'ahooks';
import { Input as AntInput } from 'antd';
import { FormListOperation } from 'antd/lib/form/FormList';
import { isEmpty, pick } from 'lodash';
import React, { FC, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FieldSectionType } from 'api/field-sections/types';
import {
  FormChoiceOption,
  FormGroupColor,
  FormQuestion,
  FormQuestionType,
} from 'api/form';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input, TextArea } from 'components/atoms/input';
import { Label } from 'components/atoms/label';
import { Switch } from 'components/atoms/switch/switch';
import { useTranslation } from 'hooks/use-translation';
import { useFieldsQueryFilter } from 'queries';
import {
  useCreateFieldOptionMutation,
  useCreateFieldQuestionMutation,
  useUpdateFieldOptionPositionMutation,
  useUpdateFieldQuestionMutation,
  useUpdateFieldQuestionPositionMutation,
} from 'queries/form-template';
import { pascalCase } from 'utils/pascal-case';
import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';
import { DropdownQuestionType, FormQuestionsList } from '../constants';
import { isFormDirty, isFormValid } from '../utils';
import { getArbitraryColor } from './options/color-dropdown';
import { SortableOptions } from './options/sortable-options';

const StyledForm = styled(Form)`
  height: 100%;
  overflow: scroll;
  padding-bottom: var(--spacing-11);

  ${Form.Item} {
    margin-bottom: 0px;
  }
`;

const FormHeaderSection = styled.div`
  padding: var(--layout-02) var(--layout-01);
  padding-bottom: 0;
`;

const RowsFormSection = styled.div`
  padding: var(--layout-01) var(--layout-01) var(--layout-02) var(--layout-01);
  border-bottom: 1px solid var(--border-color);

  && > .ant-form-item:not(:last-child) {
    padding-bottom: var(--layout-03);
  }

  && > .ant-form-item:last-child {
    padding-bottom: 0px;
  }
`;

const OptionsFormSection = styled.div`
  padding: var(--layout-02) var(--layout-01);
  border-bottom: 1px solid var(--border-color);

  && > .ant-form-item:not(:last-child) {
    padding-bottom: var(--layout-03);
  }

  && > .ant-form-item:last-child {
    padding-bottom: 0px;
  }
`;

const SecondaryFormSection = styled.div`
  padding: var(--layout-02) var(--layout-01);

  && > .ant-form-item:not(:last-child) {
    padding-bottom: var(--layout-03);
  }
`;

const DescriptionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-03);

  ${Label} {
    font-size: var(--font-size-base);
    margin-bottom: 0px;
  }
`;

const RequiredWrapper = styled(Form.Item)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-direction: row;

  .ant-form-item-control {
    flex-grow: 0;
  }

  .ant-form-item-label > label {
    font-size: var(--font-size-base);
  }
`;

const FormQuestionIconGroup = styled.div`
  display: flex;
  user-select: none;
  align-items: center;
  margin-bottom: var(--layout-02);
`;
const FormQuestionIcon = styled.div<{ backgroundColor: string }>`
  padding: var(--spacing-02);
  display: flex;
  align-items: center;
  border-radius: 2px;
  margin-right: var(--spacing-03);
  background: ${({ backgroundColor }) => backgroundColor};
`;

const formFieldsList = {
  formQuestionType: 'formQuestionType',
  questionText: 'questionText',
  description: 'description',
  required: 'required',
  questionMeta: 'questionMeta',
};

const AddOptionButton = styled(Button)`
  > .ant-btn-loading-icon {
    padding-right: var(--spacing-03);
  }
`;

const taskField = (field) => field.objectType === FieldSectionType.Task;
const isEnabled = (field) => field.isEnabled;
const taskFieldsFilter = [taskField, isEnabled];

export const MatrixFormField: FC<{
  selectedField: FormQuestion;
  templateId: number;
}> = ({ selectedField, templateId }) => {
  const [form] = Form.useForm();

  const [
    showDescription,
    { setTrue: setShowDescription, setFalse: setHideDescription },
  ] = useBoolean(!isEmpty(selectedField?.description));
  const questionTextRef = useRef<AntInput>(null);

  useEffect(() => {
    if (selectedField) {
      form.setFieldsValue({
        ...selectedField,
        description: selectedField.description ? selectedField.description : '',
      });
      if (selectedField.description) {
        setShowDescription();
      } else {
        setHideDescription();
      }
      questionTextRef.current?.focus();
    }
  }, [selectedField, form, setShowDescription, setHideDescription]);

  const canShowRequiredField = ![FormQuestionType.TextStatement].includes(
    selectedField.formQuestionType,
  );

  const { mutate: updateFormQuestionMutation } =
    useUpdateFieldQuestionMutation(templateId);

  const { mutate: updateFormOptionPositionMutation } =
    useUpdateFieldOptionPositionMutation(templateId);

  const { mutate: createQuestionMutation, isLoading: isCreateQuestionLoading } =
    useCreateFieldQuestionMutation(templateId);

  const { mutate: createOptionMutation, isLoading: isCreateOptionLoading } =
    useCreateFieldOptionMutation(templateId);

  const { mutate: updateFieldQuestionMutation } =
    useUpdateFieldQuestionPositionMutation(templateId);

  const triggerOnChange = () => {
    const formValues = form.getFieldsValue();

    if (
      isFormValid(form) &&
      isFormDirty(formValues, selectedField, Object.values(formFieldsList))
    ) {
      const fieldConfiguration = pick(
        formValues,
        Object.values(formFieldsList),
      );

      updateFormQuestionMutation({
        questionId: selectedField.questionId,
        payload: fieldConfiguration,
      });
    }
  };

  const requiredMessage = useTranslation('matrix.input.required');

  const formQuestionIconMap = useMemo(() => {
    const questionItem = FormQuestionsList.find((formQuestion) => {
      return formQuestion.value === selectedField.formQuestionType;
    });
    const iconBackgroundColor = FormGroupColor[pascalCase(questionItem?.group)];

    return {
      ...questionItem,
      iconBackgroundColor,
    };
  }, [selectedField.formQuestionType]);

  const { data: projectCustomFields } = useProjectCustomFields();

  const { data: taskFields = [] } = useFieldsQueryFilter(taskFieldsFilter);

  const associatedProjectField = useMemo(() => {
    return projectCustomFields.find(
      (field) => field.fieldId === selectedField.projectFieldId,
    );
  }, [selectedField.projectFieldId, projectCustomFields]);

  const associatedTaskField = useMemo(() => {
    return taskFields.find(
      (field) => field.fieldId === selectedField.taskFieldId,
    );
  }, [selectedField.taskFieldId, taskFields]);

  const hasAssociatedField =
    !isEmpty(associatedProjectField) || !isEmpty(associatedTaskField);

  const isOptionColorSupported = selectedField
    ? DropdownQuestionType.includes(selectedField.formQuestionType)
    : false;

  return (
    <StyledForm
      form={form}
      name="matrix_form_field_configuration"
      autoComplete="off"
      preserve={false}
      layout="vertical"
    >
      {() => (
        <>
          <FormHeaderSection>
            <FormQuestionIconGroup>
              <FormQuestionIcon
                backgroundColor={formQuestionIconMap.iconBackgroundColor}
              >
                {formQuestionIconMap.icon}
              </FormQuestionIcon>
              <FormattedMessage
                id={`form_templates.question.group.item.${formQuestionIconMap.label}`}
              />
            </FormQuestionIconGroup>
            <Form.Item name="questionId" hidden />
            <Form.Item
              name={formFieldsList.questionText}
              label={
                <FormattedMessage id="form_templates.field_configuration.field_label" />
              }
              rules={[{ required: true, message: requiredMessage }]}
            >
              <Input
                ref={questionTextRef}
                autoFocus
                onBlur={() => triggerOnChange()}
              />
            </Form.Item>
          </FormHeaderSection>
          <RowsFormSection>
            <div>
              <Label>
                <FormattedMessage id="rows" />
              </Label>
              <Form.List name="rows">
                {(fields, { add, remove, move }: FormListOperation) => {
                  const onSortEnd = (props) => {
                    const { oldIndex, newIndex } = props;

                    move(oldIndex, newIndex);
                    const { rows } = form.getFieldsValue(['rows']);

                    updateFieldQuestionMutation({
                      questionId: rows[newIndex].questionId,
                      payload: {
                        position: newIndex + 1,
                      },
                    });
                  };

                  return (
                    <>
                      <SortableOptions
                        useDragHandle
                        form={form}
                        fields={fields}
                        remove={remove}
                        onSortEnd={onSortEnd}
                        isOptionColorSupported={isOptionColorSupported}
                        templateId={templateId}
                        questionId={selectedField.questionId}
                        questionDepth={0}
                        setCreateBranchingDetails={() => {}}
                        hasAssociatedField={hasAssociatedField}
                        sortableOptionType="question"
                      />
                      {!hasAssociatedField && (
                        <Form.Item>
                          <AddOptionButton
                            type="dashed"
                            onClick={() => {
                              createQuestionMutation(
                                {
                                  formQuestionType: FormQuestionType.MatrixRow,
                                  questionText: `Row ${fields.length + 1}`,
                                  templateId,
                                  parentId: selectedField.questionId,
                                  description: '',
                                  required: true,
                                },
                                {
                                  onSuccess: (rowQuestion) => {
                                    add(rowQuestion);
                                  },
                                },
                              );
                            }}
                            block
                            loading={isCreateQuestionLoading}
                            icon={<Add16 />}
                          >
                            <FormattedMessage id="settings.fields.add_row" />
                          </AddOptionButton>
                        </Form.Item>
                      )}
                    </>
                  );
                }}
              </Form.List>
            </div>
          </RowsFormSection>
          <OptionsFormSection>
            <div>
              <Label>
                <FormattedMessage id="columns" />
              </Label>
              <Form.List name="options">
                {(fields, { add, remove, move }: FormListOperation) => {
                  const onSortEnd = ({ oldIndex, newIndex }) => {
                    move(oldIndex, newIndex);
                    const { options } = form.getFieldsValue(['options']);

                    updateFormOptionPositionMutation({
                      questionId: selectedField.questionId,
                      optionId: options[newIndex].optionId,
                      updatedOptionsList: options.map(
                        (option: FormChoiceOption, index: number) => {
                          return { ...option, position: index + 1 };
                        },
                      ),
                    });
                  };

                  return (
                    <>
                      <SortableOptions
                        useDragHandle
                        form={form}
                        fields={fields}
                        remove={remove}
                        onSortEnd={onSortEnd}
                        isOptionColorSupported={false}
                        templateId={templateId}
                        questionDepth={0}
                        questionId={selectedField.questionId}
                        hasAssociatedField={hasAssociatedField}
                        setCreateBranchingDetails={() => {}}
                        showBranch={false}
                      />
                      {!hasAssociatedField && (
                        <Form.Item>
                          <AddOptionButton
                            type="dashed"
                            onClick={() => {
                              createOptionMutation(
                                {
                                  optionLabel: `Column ${fields.length + 1}`,
                                  questionId: selectedField.questionId,
                                  color: isOptionColorSupported
                                    ? getArbitraryColor(fields.length)
                                    : undefined,
                                },
                                {
                                  onSuccess: (formColumn) => {
                                    add(formColumn);
                                  },
                                },
                              );
                            }}
                            block
                            loading={isCreateOptionLoading}
                            icon={<Add16 />}
                          >
                            <FormattedMessage id="settings.fields.add_column" />
                          </AddOptionButton>
                        </Form.Item>
                      )}
                    </>
                  );
                }}
              </Form.List>
            </div>
          </OptionsFormSection>
          <SecondaryFormSection>
            <DescriptionWrapper>
              <Label>
                <FormattedMessage id="description" />
              </Label>
              <Switch
                size="small"
                checked={showDescription}
                onChange={(value) => {
                  if (value) {
                    setShowDescription();
                  } else {
                    setHideDescription();
                    form.setFieldsValue({
                      description: '',
                    });
                    triggerOnChange();
                  }
                }}
              />
            </DescriptionWrapper>
            <Form.Item
              name={formFieldsList.description}
              hidden={!showDescription}
            >
              <TextArea
                name="description"
                onPressEnter={(event) => {
                  event.preventDefault();
                }}
                autoSize={{ minRows: 3, maxRows: 3 }}
                onBlur={() => triggerOnChange()}
              />
            </Form.Item>

            {canShowRequiredField && (
              <RequiredWrapper
                name={formFieldsList.required}
                valuePropName="checked"
                label={<FormattedMessage id="required" />}
              >
                <Switch size="small" onChange={() => triggerOnChange()} />
              </RequiredWrapper>
            )}
          </SecondaryFormSection>
        </>
      )}
    </StyledForm>
  );
};
